import { Check, ClipboardCopy } from 'lucide-vue-next'
import Pusher, { type Channel } from 'pusher-js'
import { h, watch } from 'vue'
import { useHoldReasonModal } from '@/composables/useHoldReasonModal'
import { useToast } from '@/foundation/toast/use-toast'
import { router } from '@/routes'
import { signinPusher } from '@/service/pusher.ts'
import { getExportDownloadUrl } from '@/service/tickets'
import { useAccountStore } from '@/stores/account'
import { useNotificationsStore } from '@/stores/notifications'
import { useTicketStore } from '@/stores/tickets'
import { Role } from '@/types/roles'

let pusherClient: Pusher

export const getPusher = () => {
  return pusherClient
}

export default {
  install() {
    const { toast } = useToast()
    const { openHoldReasonModal } = useHoldReasonModal()

    const pusher = new Pusher(import.meta.env.VITE_PUSHER_APP_KEY, {
      cluster: 'mt1',
      wsHost: import.meta.env.VITE_PUSHER_HOST,
      wsPort: Number(import.meta.env.VITE_PUSHER_PORT),
      wssPort: Number(import.meta.env.VITE_PUSHER_PORT),
      forceTLS: import.meta.env.VITE_PUSHER_FORCE_TLS === 'true',
      enabledTransports: ['ws', 'wss'],
      disableStats: true,
      userAuthentication: {
        endpoint: '',
        transport: 'ajax',
        customHandler: (socketIdInfo, callback) => {
          const { onSuccess } = signinPusher(socketIdInfo.socketId)

          onSuccess(({ auth, user_data }) => {
            callback(null, { auth, user_data })
          })
        },
      },
    })

    pusherClient = pusher

    const accountStore = useAccountStore()

    watch(
      () => accountStore.user,
      (user) => {
        if (!user?.id || [Role.Client, Role.ClientApprover].includes(user?.role)) return

        pusher.user.bind('export-ready', ({ exportHistoryId }: { exportHistoryId?: string }) => {
          if (!exportHistoryId) {
            console.error('export-ready event missing exportHistoryId')

            return
          }

          const result = getExportDownloadUrl(exportHistoryId)

          result.onSuccess((data: any) => {
            if (!data?.downloadUrl) {
              console.error('No downloadUrl returned for export', exportHistoryId)
              toast({
                title: 'Export Download Failed',
                description: 'Could not get a download link. Check export history to retry.',
                variant: 'destructive',
              })

              return
            }

            const url = data.downloadUrl
            const link = document.createElement('a')

            link.href = url
            link.download = ''
            link.rel = 'noopener noreferrer'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            toast({
              title: 'Export Ready',
              description: h('div', { class: 'flex flex-col gap-2' }, [
                h('span', 'Download should start automatically.'),
                h(
                  'button',
                  {
                    class: 'inline-flex items-center gap-1 text-sm font-medium underline cursor-pointer',
                    onclick: () => {
                      navigator.clipboard.writeText(url)
                      toast({ title: 'Link copied to clipboard', variant: 'success', duration: 2000 })
                    },
                  },
                  [h(ClipboardCopy, { class: 'w-3 h-3' }), 'Copy download link'],
                ),
              ]),
              variant: 'success',
              duration: 15000,
            })
          })

          result.onError((error: any) => {
            console.error('Failed to fetch export download URL:', error)
            toast({
              title: 'Export Download Failed',
              description: 'Could not generate download link. Try downloading from export history.',
              variant: 'destructive',
            })
          })
        })

        const channel: Channel = pusher.subscribe('events')

        channel.bind('ticket:created', (ticket: any) => {
          if (Role.ClientManager === user?.role && ticket.createdBy !== user.id) return

          const ticketStore = useTicketStore()

          ticketStore.addTicket(ticket)

          const isBacklog = ticket.status?.statusCategory === 'INACTIVE'
          const message = isBacklog ? 'Ticket created in backlog due to limit' : 'The ticket has been created successfully'

          toast({
            description: h('div', { class: 'flex items-start gap-4' }, [
              h(Check, { class: 'text-white mt-2' }),
              h('div', [
                h('p', { class: 'font-semibold' }, message),
                h(
                  'p',
                  {},
                  h(
                    'a',
                    {
                      class: 'text-white underline cursor-pointer',
                      onclick: () =>
                        router.push({
                          name: 'TicketDetails',
                          params: { ticketId: ticket.id },
                          query: { ticket: ticket.ticketNumber },
                        }),
                    },
                    'View the ticket',
                  ),
                ),
              ]),
            ]),
            variant: 'success',
          })
        })

        channel.bind('ticket:timer-stopped', (data: any) => {
          const ticketStore = useTicketStore()

          ticketStore.updateTicketSla(data.ticketId, data.goalType, data)
        })

        channel.bind('ticket:timer-started', (data: any) => {
          const ticketStore = useTicketStore()

          ticketStore.updateTicketSla(data.ticketId, data.goalType, data)
        })

        channel.bind('ticket:sla-closed', (data: any) => {
          const ticketStore = useTicketStore()

          ticketStore.updateTicketSla(data.ticketId, data.goalType, data)
        })

        channel.bind('ticket:goal-changed', (data: any) => {
          const ticketStore = useTicketStore()

          ticketStore.updateTicket(data)
        })

        channel.bind('ticket:undefined-sla', (data: any) => {
          const ticketStore = useTicketStore()

          ticketStore.removeSla(data.ticketId)
        })

        channel.bind(
          'ticket:metadata-updated',
          (data: {
            ticketId: string
            ticketNumber: string
            updatedFields: string[]
            updatedData: Record<string, any>
            transitionId: string
            userId: string
            timestamp: string
            source: string
          }) => {
            const ticketStore = useTicketStore()

            const currentTicket = ticketStore.tickets.find((ticket) => ticket.id === data.ticketId)

            if (currentTicket) {
              const updatedTicket = {
                ...currentTicket,
                ...data.updatedData,
                updatedAt: data.timestamp,
              }

              ticketStore.updateTicket(updatedTicket)
              if (data.userId === user?.id) {
                toast({
                  title: `Ticket ${data.ticketNumber} Updated`,
                  description: `Metadata updated: ${data.updatedFields.map((field) => field.charAt(0).toUpperCase() + field.slice(1)).join(', ')}`,
                  variant: 'info',
                })
              }
            } else {
              console.warn(`Ticket ${data.ticketId} not found in store for metadata update`)
            }
          },
        )

        channel.bind(
          'ticket:transition-success',
          (data: {
            ticketId: string
            userId: string
            fromStatusId: string
            toStatusId: string
            ticket: any
            timestamp: string
            transitionId: string | null
          }) => {
            const ticketStore = useTicketStore()

            ticketStore.updateTicket(data.ticket)

            if (data.userId === 'system-wip-auto-queue') {
              toast({
                description: h('div', { class: 'flex items-start gap-4' }, [
                  h(Check, { class: 'text-white mt-2' }),
                  h('div', [
                    h('p', { class: 'font-semibold' }, `Ticket ${data.ticket.ticketNumber} moved from backlog`),
                    h(
                      'p',
                      {},
                      h(
                        'a',
                        {
                          class: 'text-white underline cursor-pointer',
                          onclick: () =>
                            router.push({
                              name: 'TicketDetails',
                              params: { ticketId: data.ticket.id },
                              query: { ticket: data.ticket.ticketNumber },
                            }),
                        },
                        'View the ticket',
                      ),
                    ),
                  ]),
                ]),
                variant: 'success',
                duration: 5000,
              })
            }
          },
        )

        channel.bind(
          'ticket:transition-failed',
          (data: {
            ticketId: string
            userId: string
            fromStatusId: string
            toStatusId: string
            revertToStatusId: string
            mainMessage: string
            fromStatusName: string
            toStatusName: string
            errors: string[]
            failedRules: string[]
            timestamp: string
            transitionId: string
            showErrorToast: boolean
            ticketInfo: {
              title: string
              assignedToId: string | null
              categoryId: string
              priorityId: string
            }
          }) => {
            const ticketStore = useTicketStore()

            const currentTicket = ticketStore.tickets.find((ticket) => ticket.id === data.ticketId)

            if (currentTicket) {
              const revertedTicket = {
                ...currentTicket,
                status: ticketStore.statuses.find((status) => status.id === data.revertToStatusId),
                statusId: data.revertToStatusId,
                updatedAt: data.timestamp,
              }

              ticketStore.updateTicket(revertedTicket)
            }
            if (!data.showErrorToast) {
              return
            }

            if (data.userId !== user.id) return

            toast({
              title: data.mainMessage,
              description: data.errors.map((error) => ' • ' + (error.length > 200 ? error.substring(0, 200) + '...' : error)),
              variant: 'warning',
              duration: 8000,
            })
          },
        )

        channel.bind('user:notification', (data: any) => {
          if (data.userId === user?.id) {
            const notificationsStore = useNotificationsStore()

            if (data.notification) {
              notificationsStore.addNotification(data.notification)
            }

            toast({
              title: data.notification?.title || 'New Notification',
              description: data.notification?.message || 'You have a new notification',
              variant: 'info',
              action: data.notification?.actionUrl
                ? h(
                    'a',
                    {
                      class: 'text-sm font-medium underline cursor-pointer',
                      onclick: () => router.push(data.notification.actionUrl),
                    },
                    'View',
                  )
                : undefined,
            })
          }
        })

        channel.bind('notification:received', (data: any) => {
          if (data.userId === user?.id) {
            const notificationsStore = useNotificationsStore()

            if (data.notification) {
              notificationsStore.addNotification(data.notification)
            }

            toast({
              title: data.notification?.title || 'New Notification',
              description: data.notification?.message || 'You have a new notification',
              variant: 'info',
              action: data.notification?.actionUrl
                ? h(
                    'a',
                    {
                      class: 'text-sm font-medium underline cursor-pointer',
                      onclick: () => router.push(data.notification.actionUrl),
                    },
                    'View',
                  )
                : undefined,
            })
          }
        })

        channel.bind(
          'ticket:hold-reason-modal',
          (data: {
            ticketId: string
            ticketNumber: string
            userId: string
            transitionId: string
            timestamp: string
            originalStatusRequest?: any // Original request that triggered the guard
          }) => {
            if (data.userId === user?.id) {
              openHoldReasonModal({
                ticketId: data.ticketId,
                ticketNumber: data.ticketNumber,
                transitionId: data.transitionId,
                originalStatusRequest: data.originalStatusRequest, // Pass original request for retry
              })
            }
          },
        )

        // Jira sync error notifications
        channel.bind(
          'jira:sync-error',
          (data: { ticketId: string; type: 'status' | 'priority'; error: string; message: string }) => {
            toast({
              title: 'Jira Transition Forbidden',
              description: data.error || data.message,
            })
          },
        )
      },
      { immediate: true },
    )
  },
}
